import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Divider,
    IconButton,
    TextField,
    Button,
    Chip,
    Dialog,
    MenuItem,
    Slider,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { EditRounded, AddCircleOutline, CheckCircleOutline, Cancel } from '@mui/icons-material';
import { IEvent, IProject, TFAQ } from '../../../../types/global/helper';
import { useDispatch } from 'react-redux';
import { updateEvent } from '../../../../redux/events/action';
import { CustomDatePicker } from '../../../DatePicker/CustomDatePicker';
import { EventSchedule } from '../../../EventComponents/EventSchedule';
import LocationAutocomplete from '../../../LocationAutocomplete/LocationAutocomplete';
import { formatDateWithTimezone } from '../../../../utils/AppUtils';
import MediaAdderComponent from '../../../MediaAdderComponent/MediaAdderComponent';
import { ProjectPlayer } from '../../../EsCards/ProjectPlayer';
import { UploadContestAvatarGroup } from '../../../EsForm/UploadContestAvatarGroup';
import { IPartner } from '../../../../redux/discovery/types';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CloseIcon } from '@chakra-ui/icons';
import { CustomTextField } from '../../../TextInput/CustomTextField';
import { ThemeStyle } from '../../../../module/ThemeManager';
import { RichTextEditor } from '../../../Common/RichTextEditor';
import { RichTextViewer } from '../../../Common/RichTextViewer';

const REFUND_POLICY_OPTIONS = ['non-refundable', 'refund up to 7 days before the event', 'refundable at anytime'];

const EVENT_KIND_OPTIONS = [
    'watch party',
    'networking',
    'conference',
    'film festival',
    'music festival',
    'meet up',
    'dance party',
    'creative dinner',
    'theme party',
];

const DRESS_CODE_OPTIONS = ['casual', 'business casual', 'formal', 'black tie', 'costume', 'themed'];

interface EventDetailsTabProps {
    event?: IEvent;
    onEditDetails: (sectionName: string) => void;
    onVideoClick?: (video: IProject) => void;
}

const NoContentMessage = ({ message }: { message?: string }) => {
    const { themeStyle } = useThemeContext();
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                color: theme[themeStyle].light_gray_text,
                fontStyle: 'italic',
            }}>
            {message || 'No content provided'}
        </Typography>
    );
};

const DraggableRequirementItem = ({
    req,
    index,
    themeStyle,
    onRemove,
}: {
    req: string;
    index: number;
    themeStyle: ThemeStyle;
    onRemove: () => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: req });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                p: 1.5,
                backgroundColor: theme[themeStyle].gray_text,
                borderRadius: '8px',
                border: `1px solid ${theme[themeStyle].form_black}`,
                '&:hover': { backgroundColor: theme[themeStyle].form_black },
            }}>
            <Box
                {...listeners}
                {...attributes}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    cursor: 'grab',
                    color: theme[themeStyle].tag_white,
                    '&:active': { cursor: 'grabbing' },
                }}>
                <DragIndicatorIcon />
            </Box>
            <Typography
                sx={{
                    flex: 1,
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                }}>
                {req}
            </Typography>
            <IconButton
                onClick={onRemove}
                size="small"
                sx={{
                    color: theme[themeStyle].tag_white,
                    '&:hover': { color: theme[themeStyle].primary_orange },
                }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
};

interface EventDateBreakdown {
    start_date: string | Date;
    end_date: string | Date;
    detail?: string;
    location?: {
        type: string;
        coordinates: number[];
        address: string;
        description: string;
        short_name: string;
    };
}

export const EventDetailsTab: React.FC<EventDetailsTabProps> = ({ event, onEditDetails, onVideoClick }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const sensors = useSensors(useSensor(PointerSensor));

    // State for editing sections
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({
        details: false,
        description: false,
        dateLocation: false,
        host: false,
        requirements: false,
        typeTags: false,
        schedule: false,
        event_video: false,
        faq: false,
        partners: false,
        do_not_convert_currency: false,
    });

    // State for media (event_video)
    const [media, setMedia] = useState<IProject[]>([]);

    // State for FAQ
    const [faqItems, setFaqItems] = useState<TFAQ[]>([]);
    const [editingFaqIndex, setEditingFaqIndex] = useState<number | null>(null);
    const [newFaqQuestion, setNewFaqQuestion] = useState('');
    const [newFaqAnswer, setNewFaqAnswer] = useState('');

    // State for partners
    const [partners, setPartners] = useState<IPartner[]>([]);

    // State for edited values
    const [editedValues, setEditedValues] = useState<{ [key: string]: any }>({});

    // State for tags dialog
    const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    // Common tags
    const commonTags = [
        'Music',
        'Art',
        'Theater',
        'Film',
        'Dance',
        'Comedy',
        'Food',
        'Drinks',
        'Networking',
        'Education',
        'Technology',
        'Workshop',
        'Conference',
        'Festival',
    ];

    // State for requirements
    const [requirements, setRequirements] = useState<string[]>([]);
    const [newRequirement, setNewRequirement] = useState('');

    const noop = () => {};

    if (!event) {
        return (
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                    fontSize: '1rem',
                    color: theme[themeStyle].primary_white,
                }}>
                No event details available
            </Typography>
        );
    }

    const handleStartEditing = (section: string) => {
        // Initialize edited values when starting to edit a section
        let initialValues: any = {};

        switch (section) {
            case 'details':
                initialValues = {
                    name: event.name,
                    eventType: event.kind,
                    refundPolicy: event.refund_policy || '',
                    max_ticket_purchasable_per_person: event.max_ticket_purchasable_per_person || 0,
                    total_tickets: event.total_tickets || 0,
                    age_restriction: event.age_restriction || 0,
                    dress_code: event.dress_code || '',
                };
                break;
            case 'description':
                initialValues = {
                    description: event.description || '',
                };
                break;
            case 'dateLocation':
                initialValues = {
                    startDate: event.event_start_date || undefined,
                    endDate: event.event_end_date || undefined,
                    timezone: event.timezone || undefined,
                    location: event.location?.address || '',
                    locationCoordinates: event.location || undefined,
                };
                break;
            case 'host':
                initialValues = {
                    aboutHost: event.about_host || '',
                    hostContact: event.host_contact || '',
                };
                break;
            case 'requirements':
                setRequirements(event.requirements || []);
                break;
            case 'typeTags':
                initialValues = {
                    eventType: event.kind,
                };
                setSelectedTags(event.tags || []);
                break;
            case 'schedule':
                initialValues = {
                    event_date_breakdown: event.event_date_breakdown || [],
                };
                break;
            case 'event_video':
                if (event.event_video) {
                    setMedia([event.event_video as IProject]);
                } else {
                    setMedia([]);
                }
                break;
            case 'faq':
                setFaqItems(event.faq || []);
                break;
            case 'partners':
                initialValues = {
                    partners: event.partners || [],
                };
                setPartners(event.partners || []);
                break;
            case 'do_not_convert_currency':
                initialValues = {
                    do_not_convert_currency: event.do_not_convert_currency || false,
                };
                break;
        }

        setEditedValues({ ...editedValues, ...initialValues });
        setEditingSections({ ...editingSections, [section]: true });
    };

    const handleCancelEditing = (section: string) => {
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleSaveChanges = (section: string) => {
        let updatePayload: any = {};

        switch (section) {
            case 'details':
                updatePayload = {
                    name: editedValues.name,
                    kind: editedValues.eventType,
                    refund_policy: editedValues.refundPolicy,
                    max_ticket_purchasable_per_person: editedValues.max_ticket_purchasable_per_person,
                    total_tickets: editedValues.total_tickets,
                    age_restriction: editedValues.age_restriction,
                    dress_code: editedValues.dress_code,
                };
                break;
            case 'description':
                updatePayload = {
                    description: editedValues.description,
                };
                break;
            case 'dateLocation':
                updatePayload = {
                    event_start_date: editedValues.startDate,
                    event_end_date: editedValues.endDate,
                    timezone: editedValues.timezone,
                };
                if (editedValues.locationCoordinates) {
                    updatePayload.location = editedValues.locationCoordinates;
                }
                break;
            case 'host':
                updatePayload = {
                    about_host: editedValues.aboutHost,
                    host_contact: editedValues.hostContact,
                };
                break;
            case 'requirements':
                updatePayload = {
                    requirements: requirements,
                };
                break;
            case 'typeTags':
                updatePayload = {
                    kind: editedValues.eventType,
                    tags: selectedTags,
                };
                break;
            case 'schedule':
                updatePayload = {
                    event_date_breakdown: editedValues.event_date_breakdown,
                };
                break;
            case 'event_video':
                if (media.length > 0) {
                    updatePayload = {
                        event_video: media[0]._id,
                    };
                } else {
                    updatePayload = {
                        event_video: null,
                    };
                }
                break;
            case 'faq':
                updatePayload = {
                    faq: faqItems,
                };
                break;
            case 'partners':
                updatePayload = {
                    partners: partners.map(p => p._id || p),
                };
                break;
            case 'do_not_convert_currency':
                updatePayload = {
                    do_not_convert_currency: editedValues.do_not_convert_currency,
                };
                break;
        }

        // Dispatch update event action
        if (event._id) {
            dispatch(
                updateEvent({
                    id: event._id,
                    body: updatePayload,
                }),
            );
        }

        // Turn off editing for this section
        setEditingSections({ ...editingSections, [section]: false });
        setEditingFaqIndex(null);
    };

    const handleInputChange = (field: string, value: any) => {
        // For date fields, ensure we store the string value as-is without any conversion
        if (field === 'startDate' || field === 'endDate') {
            // Store the date string exactly as received, no conversion
            setEditedValues({ ...editedValues, [field]: typeof value === 'string' ? value : String(value) });
        } else {
            setEditedValues({ ...editedValues, [field]: value });
        }
    };

    const handleBreakdownChange = (index: number, field: string, value: any) => {
        const newBreakdown = [...(editedValues.event_date_breakdown || [])];
        newBreakdown[index] = { ...newBreakdown[index], [field]: value };
        handleInputChange('event_date_breakdown', newBreakdown);
    };

    const handleAddBreakdown = () => {
        const newBreakdown = [...(editedValues.event_date_breakdown || [])];
        newBreakdown.push({
            start_date: new Date(),
            end_date: new Date(),
            detail: '',
            location: {
                type: 'Point',
                coordinates: [],
                address: '',
                description: '',
                short_name: '',
            },
        });
        handleInputChange('event_date_breakdown', newBreakdown);
    };

    const handleAddRequirement = () => {
        if (newRequirement.trim()) {
            const updated = [...requirements, newRequirement.trim()];
            setRequirements(updated);
            handleInputChange('requirements', updated);
            setNewRequirement('');
            onEditDetails('requirements');
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !selectedTags.includes(newTag.trim())) {
            setSelectedTags([...selectedTags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleSelectTag = (tag: string) => {
        if (!selectedTags.includes(tag)) {
            setSelectedTags([...selectedTags, tag]);
        } else {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        }
    };

    const handleAddFaq = () => {
        if (newFaqQuestion.trim() && newFaqAnswer.trim()) {
            setFaqItems([...faqItems, { question: newFaqQuestion.trim(), answer: newFaqAnswer.trim() }]);
            setNewFaqQuestion('');
            setNewFaqAnswer('');
        }
    };

    const handleEditFaq = (index: number) => {
        setEditingFaqIndex(index);
        setNewFaqQuestion(faqItems[index].question);
        setNewFaqAnswer(faqItems[index].answer);
    };

    const handleUpdateFaq = () => {
        if (editingFaqIndex !== null && newFaqQuestion.trim() && newFaqAnswer.trim()) {
            const updatedFaq = [...faqItems];
            updatedFaq[editingFaqIndex] = { question: newFaqQuestion.trim(), answer: newFaqAnswer.trim() };
            setFaqItems(updatedFaq);
            setEditingFaqIndex(null);
            setNewFaqQuestion('');
            setNewFaqAnswer('');
        }
    };

    const handleDeleteFaq = (index: number) => {
        const updatedFaq = faqItems.filter((_, i) => i !== index);
        setFaqItems(updatedFaq);
    };

    const handleCancelFaqEdit = () => {
        setEditingFaqIndex(null);
        setNewFaqQuestion('');
        setNewFaqAnswer('');
    };

    const SectionHeader = ({ title, section }: { title: string; section: string }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                }}>
                {title}
            </Typography>

            {editingSections[section] ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => handleCancelEditing(section)}
                        sx={{
                            borderColor: theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].primary_white,
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: theme[themeStyle].placeholderGray,
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline sx={{ color: theme[themeStyle].black }} />}
                        onClick={() => handleSaveChanges(section)}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                        }}>
                        Save
                    </Button>
                </Box>
            ) : (
                <IconButton
                    onClick={() => {
                        handleStartEditing(section);
                        onEditDetails(section);
                    }}
                    sx={{
                        color: theme[themeStyle].primary_orange,
                    }}>
                    <EditRounded />
                </IconButton>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                py: 3,
                bgcolor: theme[themeStyle].pure_black,
                borderRadius: 2,
                width: '100%',
            }}>
            {/* Event Details */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Details" section="details" />

                {!editingSections.details ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Name
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.kind || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Refund Policy
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.refund_policy || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Max Tickets Per Person
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.max_ticket_purchasable_per_person || 'No limit'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Total Tickets
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.total_tickets || 'Unlimited'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Age Restriction
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.age_restriction
                                    ? `${event.age_restriction} years and above`
                                    : 'No age restriction'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Dress Code
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.dress_code || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Name
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Event Name"
                                value={editedValues.name || ''}
                                onChange={e => handleInputChange('name', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.eventType || ''}
                                onChange={e => handleInputChange('eventType', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {EVENT_KIND_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Refund Policy
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.refundPolicy || ''}
                                onChange={e => handleInputChange('refundPolicy', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {REFUND_POLICY_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Max Tickets Per Person
                            </Typography>
                            <TextField
                                type="number"
                                fullWidth
                                value={editedValues.max_ticket_purchasable_per_person || ''}
                                onChange={e =>
                                    handleInputChange('max_ticket_purchasable_per_person', parseInt(e.target.value))
                                }
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Total Tickets
                            </Typography>
                            <TextField
                                type="number"
                                fullWidth
                                value={editedValues.total_tickets || ''}
                                onChange={e => handleInputChange('total_tickets', parseInt(e.target.value))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Age Restriction
                            </Typography>
                            <Slider
                                value={editedValues.age_restriction || 0}
                                onChange={(_, value) => handleInputChange('age_restriction', value)}
                                min={0}
                                max={100}
                                valueLabelDisplay="auto"
                                sx={{
                                    color: theme[themeStyle].primary_orange,
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].light_gray_text,
                                    textAlign: 'center',
                                }}>
                                {editedValues.age_restriction || 0} years and above
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Dress Code
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.dress_code || ''}
                                onChange={e => handleInputChange('dress_code', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {DRESS_CODE_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Description */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Description" section="description" />

                {!editingSections.description ? (
                    <RichTextViewer theme={themeStyle} content={event.description || 'No description provided'} />
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <RichTextEditor
                            value={editedValues.description || ''}
                            onChange={(content: string) => {
                                handleInputChange('description', content);
                                onEditDetails('description');
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Date & Location */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Date & Location" section="dateLocation" />

                {!editingSections.dateLocation ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Start Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.event_start_date
                                    ? formatDateWithTimezone(event.event_start_date, event.timezone)
                                    : 'Not set'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                End Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.event_end_date
                                    ? formatDateWithTimezone(event.event_end_date, event.timezone)
                                    : 'Not set'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Location
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.location?.address || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Start Date
                            </Typography>
                            <CustomDatePicker
                                value={editedValues.startDate || undefined}
                                timezone={editedValues.timezone || event.timezone}
                                onChange={(date, timezone) => {
                                    handleInputChange('startDate', String(date));
                                    // Only update timezone if it actually changed
                                    if (timezone && timezone !== (editedValues.timezone || event.timezone)) {
                                        handleInputChange('timezone', timezone);
                                    }
                                }}
                                withTime
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                End Date
                            </Typography>
                            <CustomDatePicker
                                value={editedValues.endDate || undefined}
                                timezone={editedValues.timezone || event.timezone}
                                onChange={(date, timezone) => {
                                    handleInputChange('endDate', String(date));
                                    // Only update timezone if it actually changed
                                    if (timezone && timezone !== (editedValues.timezone || event.timezone)) {
                                        handleInputChange('timezone', timezone);
                                    }
                                }}
                                withTime
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Location
                            </Typography>
                            <LocationAutocomplete
                                value={editedValues.locationCoordinates}
                                onSelect={(address, coordinates, formattedAddress) => {
                                    handleInputChange('location', formattedAddress);
                                    handleInputChange('locationCoordinates', formattedAddress);
                                }}
                                onSelectLocationOption={noop}
                                placeholder="Search Places ..."
                                withCordinates
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* About Host */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="About Host" section="host" />

                {!editingSections.host ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                About Host
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    whiteSpace: 'pre-wrap',
                                    mb: 2,
                                }}>
                                {event.about_host || 'No host information provided'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Host Contact
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.host_contact || 'No contact information provided'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                About Host
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="About the Host"
                                value={editedValues.aboutHost || ''}
                                onChange={e => handleInputChange('aboutHost', e.target.value)}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Host Contact
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Host Contact Information"
                                value={editedValues.hostContact || ''}
                                onChange={e => handleInputChange('hostContact', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Requirements */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Requirements" section="requirements" />

                {!editingSections.requirements ? (
                    <Box>
                        {event.requirements && event.requirements.length > 0 ? (
                            event.requirements.map((req, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: theme[themeStyle].gray_text,
                                        padding: 2,
                                        borderRadius: 1,
                                        mb: 1,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {req}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <NoContentMessage message="No requirements added yet" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={event => {
                                const { active, over } = event;
                                if (active.id !== over?.id) {
                                    const oldIndex = requirements.indexOf(active.id as string);
                                    const newIndex = requirements.indexOf(over?.id as string);
                                    const newReqs = arrayMove(requirements, oldIndex, newIndex);
                                    setRequirements(newReqs);
                                    handleInputChange('requirements', newReqs);
                                    onEditDetails('requirements');
                                }
                            }}>
                            <SortableContext items={requirements} strategy={verticalListSortingStrategy}>
                                {requirements.map((req, index) => (
                                    <DraggableRequirementItem
                                        key={req}
                                        req={req}
                                        index={index}
                                        themeStyle={themeStyle}
                                        onRemove={() => {
                                            const updated = requirements.filter(r => r !== req);
                                            setRequirements(updated);
                                            handleInputChange('requirements', updated);
                                            onEditDetails('requirements');
                                        }}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex' }}>
                                <CustomTextField
                                    placeholder="Add new requirement"
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            value: newRequirement,
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                                                setNewRequirement(e.target.value),
                                            style: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                borderRadius: '8px',
                                                color: theme[themeStyle].primary_white,
                                            },
                                            onKeyPress: (e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddRequirement();
                                                }
                                            },
                                        },
                                    }}
                                />
                                <IconButton
                                    onClick={handleAddRequirement}
                                    sx={{
                                        ml: 1,
                                        color: theme[themeStyle].tag_white,
                                        '&:hover': {
                                            color: theme[themeStyle].primary_orange,
                                        },
                                    }}>
                                    <AddIcon />
                                </IconButton>
                            </Box>
                            <Typography
                                sx={{
                                    mt: 1,
                                    fontSize: '0.75rem',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].light_gray_text,
                                    fontStyle: 'italic',
                                }}>
                                To add a link, use:{' '}
                                <code style={{ color: theme[themeStyle].primary_orange }}>
                                    {'<link text="Link Text">url<>'}
                                </code>
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Event Type & Tags */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Type & Tags" section="typeTags" />

                {!editingSections.typeTags ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.kind || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {event.tags && event.tags.length > 0 ? (
                                    event.tags.map((tag, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                borderRadius: 1,
                                                padding: '4px 8px',
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {tag}
                                        </Box>
                                    ))
                                ) : (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        No tags
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.eventType || ''}
                                onChange={e => handleInputChange('eventType', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {EVENT_KIND_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {selectedTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutline />}
                                onClick={() => setTagsDialogOpen(true)}
                                sx={{
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_white,
                                    '&:hover': {
                                        borderColor: theme[themeStyle].primary_orange,
                                        backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                    },
                                }}>
                                Add Tags
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* Event Schedule */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Schedule" section="schedule" />

                {!editingSections.schedule ? (
                    <EventSchedule event={event} />
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {editedValues.event_date_breakdown?.map((breakdown: EventDateBreakdown, index: number) => (
                            <Grid item xs={12} key={index}>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme[themeStyle].form_black}`,
                                        borderRadius: 1,
                                    }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                Start Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={breakdown.start_date as string}
                                                timezone={editedValues.timezone || event.timezone}
                                                onChange={(date, timezone) => {
                                                    handleBreakdownChange(index, 'start_date', date);
                                                    // Only update timezone if it actually changed
                                                    if (
                                                        timezone &&
                                                        timezone !== (editedValues.timezone || event.timezone)
                                                    ) {
                                                        handleInputChange('timezone', timezone);
                                                    }
                                                }}
                                                minDate={new Date()}
                                                withTime
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                End Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={breakdown.end_date as string}
                                                timezone={editedValues.timezone || event.timezone}
                                                onChange={(date, timezone) => {
                                                    handleBreakdownChange(index, 'end_date', date);
                                                    // Only update timezone if it actually changed
                                                    if (
                                                        timezone &&
                                                        timezone !== (editedValues.timezone || event.timezone)
                                                    ) {
                                                        handleInputChange('timezone', timezone);
                                                    }
                                                }}
                                                minDate={breakdown.start_date}
                                                withTime
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                Details
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={breakdown.detail || ''}
                                                onChange={e => handleBreakdownChange(index, 'detail', e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        color: theme[themeStyle].tag_white,
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutline />}
                                onClick={handleAddBreakdown}
                                sx={{
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Add Schedule Item
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Event Video */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Video" section="event_video" />

                {!editingSections.event_video ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].form_black,
                            },
                        }}
                        onClick={() => handleStartEditing('event_video')}>
                        {event.event_video && (event.event_video as IProject).hls ? (
                            <ProjectPlayer
                                project={event.event_video as IProject}
                                onPlay={() => onVideoClick && onVideoClick(event.event_video as IProject)}
                            />
                        ) : (
                            <NoContentMessage />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <MediaAdderComponent
                            mediaValues={media}
                            isEdit={false}
                            mediaType={'projects'}
                            maxSelection={1}
                            title=""
                            onDelete={payload => {
                                const selections = media.filter((sel: any) => sel._id !== payload._id);
                                setMedia(selections);
                            }}
                            onSubmit={payload => {
                                setMedia(payload);
                            }}
                            disableAddButton
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* FAQ */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="FAQ" section="faq" />

                {!editingSections.faq ? (
                    <Box>
                        {event.faq && event.faq.length > 0 ? (
                            event.faq.map((faq, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        borderRadius: 1,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                            mb: 1,
                                        }}>
                                        {faq.question}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].light_gray_text,
                                        }}>
                                        {faq.answer}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <NoContentMessage message="No FAQ items added yet" />
                        )}
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].light_gray_text,
                                        mb: 1,
                                    }}>
                                    {editingFaqIndex !== null ? 'Edit FAQ Item' : 'Add New FAQ Item'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Question"
                                    value={newFaqQuestion}
                                    onChange={e => setNewFaqQuestion(e.target.value)}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Answer"
                                    value={newFaqAnswer}
                                    onChange={e => setNewFaqAnswer(e.target.value)}
                                    multiline
                                    rows={3}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={editingFaqIndex !== null ? handleUpdateFaq : handleAddFaq}
                                        disabled={!newFaqQuestion.trim() || !newFaqAnswer.trim()}
                                        sx={{
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        {editingFaqIndex !== null ? 'Update' : 'Add'}
                                    </Button>
                                    {editingFaqIndex !== null && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleCancelFaqEdit}
                                            sx={{
                                                borderColor: theme[themeStyle].placeholderGray,
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            </Box>

                            <Box>
                                {faqItems.map((faq, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            mb: 2,
                                            p: { xs: 1.5, sm: 2 },
                                            borderRadius: 1,
                                            backgroundColor: theme[themeStyle].gray_text,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                        }}>
                                        <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].primary_white,
                                                    mb: 1,
                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                    wordBreak: 'break-word',
                                                }}>
                                                {faq.question}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    color: theme[themeStyle].light_gray_text,
                                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                                    wordBreak: 'break-word',
                                                }}>
                                                {faq.answer}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                ml: { xs: 0, sm: 2 },
                                                mt: { xs: 1, sm: 0 },
                                                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                                            }}>
                                            <IconButton
                                                onClick={() => handleEditFaq(index)}
                                                size="small"
                                                sx={{
                                                    color: theme[themeStyle].primary_orange,
                                                }}>
                                                <EditRounded fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteFaq(index)}
                                                size="small"
                                                sx={{
                                                    color: theme[themeStyle].placeholderGray,
                                                }}>
                                                <Cancel fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}

                                {faqItems.length === 0 && <NoContentMessage message="No FAQ items added yet" />}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Partners */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Partners" section="partners" />

                {!editingSections.partners ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: `1px dashed ${theme[themeStyle].form_black}`,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].form_black,
                                borderColor: theme[themeStyle].primary_orange,
                            },
                        }}
                        onClick={() => handleStartEditing('partners')}>
                        {event.partners && event.partners.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {event.partners.map((partner: IPartner, index: number) => (
                                    <Chip
                                        key={index}
                                        label={partner.name}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: theme[themeStyle].light_gray_text,
                                    fontStyle: 'italic',
                                }}>
                                <AddIcon sx={{ fontSize: 16, color: theme[themeStyle].primary_orange }} />
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    Click to add partners
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <UploadContestAvatarGroup
                            value={partners as any}
                            title="Select Partner(s)"
                            searchType="partner"
                            disableTitle={false}
                            isEditMode={true}
                            onChange={selectedPartners => {
                                setPartners(selectedPartners as IPartner[]);
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Do Not Convert Currency */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Currency Conversion" section="do_not_convert_currency" />

                {!editingSections.do_not_convert_currency ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Do Not Convert Currency
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.do_not_convert_currency ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Do Not Convert Currency
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.do_not_convert_currency ? 'true' : 'false'}
                                onChange={e => handleInputChange('do_not_convert_currency', e.target.value === 'true')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                <MenuItem value="false">No</MenuItem>
                                <MenuItem value="true">Yes</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* Tags Dialog */}
            <Dialog
                open={tagsDialogOpen}
                onClose={() => setTagsDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].pure_black,
                        color: theme[themeStyle].primary_white,
                        width: '100%',
                        maxWidth: 500,
                    },
                }}>
                <Box sx={{ p: 3 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.25rem',
                            mb: 2,
                        }}>
                        Select Tags
                    </Typography>

                    {/* Add custom tag */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <TextField
                            fullWidth
                            placeholder="Add a custom tag"
                            value={newTag}
                            onChange={e => setNewTag(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].gray_text,
                                },
                            }}
                            onKeyPress={e => {
                                if (e.key === 'Enter') {
                                    handleAddTag();
                                    e.preventDefault();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddTag}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_orange,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Add
                        </Button>
                    </Box>

                    {/* Common tags */}
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: '1rem',
                            mb: 1,
                        }}>
                        Common Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {commonTags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                onClick={() => handleSelectTag(tag)}
                                sx={{
                                    backgroundColor: selectedTags.includes(tag)
                                        ? theme[themeStyle].primary_orange
                                        : theme[themeStyle].gray_text,
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>

                    {/* Selected tags */}
                    {selectedTags.length > 0 && (
                        <>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '1rem',
                                    mb: 1,
                                }}>
                                Selected Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                {selectedTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setTagsDialogOpen(false)}
                            sx={{
                                borderColor: theme[themeStyle].placeholderGray,
                                color: theme[themeStyle].primary_white,
                            }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setTagsDialogOpen(false)}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_orange,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Done
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};
